import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown } from "lucide-react";
import ExportBar from "@/components/ExportBar";
import { formatCurrency } from "@/lib/formatters";
import { generateAnnualPLPDF } from "@/lib/plStatementPDF";
import * as db from "@/lib/db";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const yearBounds = (year) => ({ start: `${year}-01-01`, end: `${year}-12-31` });

const AnnualPL = () => {
  const today = new Date();
  const [year, setYear] = useState(String(today.getFullYear()));
  const [availableYears, setAvailableYears] = useState([]);
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStatic = useCallback(async () => {
    try {
      const [tt, cats, years] = await Promise.all([
        db.listTransactionTypes({ includeArchived: true }),
        db.listTransactionCategories({ includeArchived: true }),
        db.listEventYears(),
      ]);
      setTypes(tt); setCategories(cats);
      const yset = new Set(years);
      yset.add(String(today.getFullYear()));
      setAvailableYears(Array.from(yset).sort((a, b) => b.localeCompare(a)));
    } catch (e) { console.error(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchYear = useCallback(async () => {
    try {
      setLoading(true);
      const { start, end } = yearBounds(year);
      const [evs, ts] = await Promise.all([
        db.listEventsSummary({ year, sortBy: "latest", eventStatus: "completed" }),
        db.listTransactions({ from: start, to: end, sortBy: "date_asc" }),
      ]);
      setEvents(evs); setTxs(ts);
    } catch (e) {
      console.error(e); toast.error("Failed to load annual data");
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchStatic(); }, [fetchStatic]);
  useEffect(() => { fetchYear(); }, [fetchYear]);

  const typeById = useCallback((id) => types.find((t) => t.id === id), [types]);
  const catById  = useCallback((id) => categories.find((c) => c.id === id), [categories]);

  // Bucket each event / transaction into a 1-12 month.
  const monthOf = (dateStr) => (dateStr ? Number(dateStr.slice(5, 7)) : 0);

  const monthly = useMemo(() => {
    const empty = () => ({
      revenue: 0, productCost: 0, eventExpenses: 0,
      operating: 0, woodIncentive: 0,
      ownerDraws: 0,
      // Detailed event-side buckets (for Annual Schedule B)
      evRental: 0, evStaff: 0, evTransport: 0, evOther: 0,
      // Cash-only buckets (for Annual Schedule D)
      productPurchases: 0, assetPurchases: 0,
      pendingInflow: 0, pendingOutflow: 0,
    });
    const perMonth = Array.from({ length: 12 }, empty);
    events.forEach((e) => {
      const m = monthOf(e.startDate) - 1;
      if (m < 0 || m > 11) return;
      perMonth[m].revenue     += Number(e.totalSalesCollected || 0);
      perMonth[m].productCost += Number(e.totalProductCost || 0);
      perMonth[m].evRental    += Number(e.rentalCost || 0);
      perMonth[m].evStaff     += Number(e.totalStaffWages || 0);
      perMonth[m].evTransport += Number(e.transportCost || 0);
      perMonth[m].evOther     += Number(e.otherCost || 0);
      perMonth[m].eventExpenses +=
        Number(e.rentalCost || 0) + Number(e.transportCost || 0) +
        Number(e.otherCost || 0) + Number(e.totalStaffWages || 0);
    });
    txs.forEach((t) => {
      const m = monthOf(t.txDate) - 1;
      if (m < 0 || m > 11) return;
      const tp = typeById(t.transactionTypeId);
      const pl = tp?.plTreatment;
      const amt = Number(t.amount || 0);
      if (pl === "operating_expense") {
        perMonth[m].operating += amt;
        const isWood = (tp?.name || "").toLowerCase().includes("wood");
        if (isWood) perMonth[m].woodIncentive += amt;
      } else if (pl === "owner_draw") {
        perMonth[m].ownerDraws += amt;
      } else if (pl === "product_purchase_cash_only") {
        perMonth[m].productPurchases += amt;
      } else if (pl === "asset_capitalized") {
        perMonth[m].assetPurchases += amt;
      } else if (!pl || pl === "pending") {
        if (t.cashFlowDirection === "inflow")  perMonth[m].pendingInflow  += amt;
        if (t.cashFlowDirection === "outflow") perMonth[m].pendingOutflow += amt;
      }
    });
    // Compute derived numbers per month.
    return perMonth.map((r) => {
      const grossProfit = r.revenue - r.productCost;
      const grossMargin = r.revenue > 0 ? (grossProfit / r.revenue) * 100 : 0;
      const netProfit = r.revenue - r.productCost - r.eventExpenses - r.operating;
      const netMargin = r.revenue > 0 ? (netProfit / r.revenue) * 100 : 0;
      const retained = netProfit - r.ownerDraws;
      return { ...r, grossProfit, grossMargin, netProfit, netMargin, retained };
    });
  }, [events, txs, typeById]);

  // Annual totals.
  const annual = useMemo(() => {
    const t = monthly.reduce((acc, m) => ({
      revenue: acc.revenue + m.revenue,
      productCost: acc.productCost + m.productCost,
      eventExpenses: acc.eventExpenses + m.eventExpenses,
      operating: acc.operating + m.operating,
      woodIncentive: acc.woodIncentive + m.woodIncentive,
      ownerDraws: acc.ownerDraws + m.ownerDraws,
      evRental: acc.evRental + m.evRental,
      evStaff: acc.evStaff + m.evStaff,
      evTransport: acc.evTransport + m.evTransport,
      evOther: acc.evOther + m.evOther,
      productPurchases: acc.productPurchases + m.productPurchases,
      assetPurchases: acc.assetPurchases + m.assetPurchases,
      pendingInflow: acc.pendingInflow + m.pendingInflow,
      pendingOutflow: acc.pendingOutflow + m.pendingOutflow,
    }), {
      revenue: 0, productCost: 0, eventExpenses: 0, operating: 0, woodIncentive: 0, ownerDraws: 0,
      evRental: 0, evStaff: 0, evTransport: 0, evOther: 0,
      productPurchases: 0, assetPurchases: 0, pendingInflow: 0, pendingOutflow: 0,
    });
    const grossProfit = t.revenue - t.productCost;
    const grossMargin = t.revenue > 0 ? (grossProfit / t.revenue) * 100 : 0;
    const netProfit = t.revenue - t.productCost - t.eventExpenses - t.operating;
    const netMargin = t.revenue > 0 ? (netProfit / t.revenue) * 100 : 0;
    const retained = netProfit - t.ownerDraws;
    return { ...t, grossProfit, grossMargin, netProfit, netMargin, retained };
  }, [monthly]);

  // Annual OPEX grouped by category (for PDF Schedule B).
  const annualOpexByCategory = useMemo(() => {
    const map = new Map();
    txs.forEach((t) => {
      const tp = typeById(t.transactionTypeId);
      if (tp?.plTreatment !== "operating_expense") return;
      const cat = catById(t.categoryId);
      const key = cat?.name || "Uncategorised";
      map.set(key, (map.get(key) || 0) + Number(t.amount || 0));
    });
    return Array.from(map.entries())
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [txs, typeById, catById]);

  // Insight numbers.
  const insights = useMemo(() => {
    const withData = monthly
      .map((m, i) => ({ ...m, monthIdx: i }))
      .filter((m) => m.revenue !== 0 || m.operating !== 0 || m.eventExpenses !== 0 || m.productCost !== 0);
    if (withData.length === 0) {
      return { best: null, worst: null, highestRev: null, largestCat: null };
    }
    const best = [...withData].sort((a, b) => b.netProfit - a.netProfit)[0];
    const worst = [...withData].sort((a, b) => a.netProfit - b.netProfit)[0];
    const highestRev = [...withData].sort((a, b) => b.revenue - a.revenue)[0];

    // Aggregate operating expenses by category across the year.
    const catTotals = new Map();
    txs.forEach((t) => {
      const tp = typeById(t.transactionTypeId);
      if (tp?.plTreatment !== "operating_expense") return;
      const cat = catById(t.categoryId);
      const key = cat?.name || "Uncategorised";
      catTotals.set(key, (catTotals.get(key) || 0) + Number(t.amount || 0));
    });
    let largestCat = null;
    catTotals.forEach((v, k) => {
      if (!largestCat || v > largestCat.amount) largestCat = { name: k, amount: v };
    });
    return { best, worst, highestRev, largestCat };
  }, [monthly, txs, typeById, catById]);

  const selectClass = "w-full sm:w-56 bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 px-3 text-sm text-slate-800 appearance-none cursor-pointer transition-colors";

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="annual-pl-title">
              Annual P&amp;L
            </h1>
            <p className="text-sm text-slate-600 mt-1">Full-year profit &amp; loss with month-by-month breakdown</p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">Year</label>
            <select value={year} onChange={(e) => setYear(e.target.value)} className={selectClass} data-testid="annual-year-select">
              {availableYears.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
            <div className="mt-3">
              <ExportBar
                testIdPrefix="annual-export"
                pdfOverride={() => {
                  const eventExpensesPayload = {
                    rental: annual.evRental,
                    staff: annual.evStaff,
                    commission: 0,
                    transport: annual.evTransport,
                    marketing: 0,
                    other: annual.evOther,
                    total: annual.eventExpenses,
                  };
                  const companyOpexPayload = {
                    items: annualOpexByCategory.map((r) => ({ name: r.category, amount: r.amount })),
                    total: annual.operating,
                  };
                  const monthlyComparison = monthly.map((m, i) => ({
                    month: MONTH_LABELS[i],
                    revenue: m.revenue,
                    productCost: m.productCost,
                    grossProfit: m.grossProfit,
                    eventExpenses: m.eventExpenses,
                    companyOpex: m.operating,
                    netProfit: m.netProfit,
                    netMargin: m.netMargin,
                  }));
                  const eventExpenseBreakdown = [
                    { category: "Event Rental", amount: annual.evRental },
                    { category: "Staff Wages / Event Manpower", amount: annual.evStaff },
                    { category: "Transport Expense", amount: annual.evTransport },
                    { category: "Other Event Expenses", amount: annual.evOther },
                  ].filter((r) => r.amount !== 0);

                  generateAnnualPLPDF({
                    year,
                    totalRevenue: annual.revenue,
                    totalProductCost: annual.productCost,
                    grossProfit: annual.grossProfit,
                    grossMargin: annual.grossMargin,
                    eventExpenses: eventExpensesPayload,
                    companyOpex: companyOpexPayload,
                    netProfit: annual.netProfit,
                    netMargin: annual.netMargin,
                    ownerDraws: annual.ownerDraws,
                    profitRetained: annual.retained,
                    cashSummary: {
                      openingCash: 0,
                      cashReceived: annual.revenue + annual.pendingInflow,
                      opexPaid: annual.operating,
                      productPurchases: annual.productPurchases,
                      assetPurchases: annual.assetPurchases,
                      ownerWithdrawals: annual.ownerDraws,
                      pendingInflow: annual.pendingInflow,
                      pendingOutflow: annual.pendingOutflow,
                      netCashMovement:
                        (annual.revenue + annual.pendingInflow)
                        - (annual.operating + annual.productPurchases + annual.assetPurchases + annual.ownerDraws + annual.pendingOutflow),
                      closingCash:
                        (annual.revenue + annual.pendingInflow)
                        - (annual.operating + annual.productPurchases + annual.assetPurchases + annual.ownerDraws + annual.pendingOutflow),
                    },
                    monthlyComparison,
                    eventExpenseBreakdown,
                    annualOpexBreakdown: annualOpexByCategory,
                  });
                }}
                buildReport={() => {
                  const monthCols = [
                    { key: "month", label: "Month" },
                    { key: "revenue", label: "Revenue", type: "currency" },
                    { key: "grossProfit", label: "Gross Profit", type: "currency" },
                    { key: "operating", label: "Op. Exp. + Event Exp.", type: "currency" },
                    { key: "netProfit", label: "Net Profit", type: "currency" },
                    { key: "netMargin", label: "Net Margin %" },
                    { key: "ownerDraws", label: "Owner Draws", type: "currency" },
                    { key: "retained", label: "Retained", type: "currency" },
                  ];
                  const rows = monthly.map((m, i) => ({
                    month: MONTH_LABELS[i],
                    revenue: m.revenue,
                    grossProfit: m.grossProfit,
                    operating: m.operating + m.eventExpenses,
                    netProfit: m.netProfit,
                    netMargin: m.revenue > 0 ? `${m.netMargin.toFixed(1)}%` : "—",
                    ownerDraws: m.ownerDraws,
                    retained: m.retained,
                  }));
                  const totals = {
                    month: "YEAR TOTAL",
                    revenue: annual.revenue,
                    grossProfit: annual.grossProfit,
                    operating: annual.operating + annual.eventExpenses,
                    netProfit: annual.netProfit,
                    netMargin: annual.revenue > 0 ? `${annual.netMargin.toFixed(1)}%` : "—",
                    ownerDraws: annual.ownerDraws,
                    retained: annual.retained,
                  };
                  return {
                    title: `Annual P&L ${year}`,
                    subtitle: `January 1, ${year} — December 31, ${year}`,
                    filename: `Gao-Chill-Annual-PL-${year}`,
                    columns: monthCols,
                    rows, totals,
                    sections: [{ heading: "12-Month Breakdown", columns: monthCols, rows, totals }],
                    metrics: [
                      { label: "Annual Revenue", value: formatCurrency(annual.revenue) },
                      { label: "Net Profit", value: formatCurrency(annual.netProfit) },
                      { label: "Profit Retained", value: formatCurrency(annual.retained) },
                    ],
                    footnote: "Product & Asset Purchases and Owner Withdrawals are excluded from Net Profit. See Monthly P&L Cash Summary for cash flows.",
                  };
                }}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Calculating…</div>
        ) : (
          <>
            {/* Headline P&L */}
            <section className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] text-white rounded-2xl p-5 sm:p-6 shadow-lg space-y-4" data-testid="annual-pl-block">
              <PLRow label={`${year} Total Revenue`} value={annual.revenue} big testId="ann-revenue" />
              <PLRow label="Less: Product Cost" value={-annual.productCost} testId="ann-product-cost" />
              <PLRow label="Annual Gross Profit" value={annual.grossProfit} bold testId="ann-gross-profit" />
              <PLRow label="Gross Margin" value={`${annual.grossMargin.toFixed(2)}%`} text testId="ann-gross-margin" />
              <div className="border-t border-white/20 pt-3 space-y-2">
                <PLRow label="Less: Event Expenses" value={-annual.eventExpenses} testId="ann-event-expenses" />
                <PLRow label="Less: Operating Expenses (incl. Wood Incentive)" value={-annual.operating} testId="ann-operating" />
                <PLRow label="  of which Wood Profit-based Incentive" value={-annual.woodIncentive} testId="ann-wood-incentive" />
              </div>
              <div className="border-t border-white/30 pt-3">
                <PLRow label="Annual Net Profit / Loss" value={annual.netProfit} big bold
                  color={annual.netProfit >= 0 ? "text-emerald-300" : "text-red-300"}
                  testId="ann-net-profit" />
                <PLRow label="Net Margin" value={`${annual.netMargin.toFixed(2)}%`} text testId="ann-net-margin" />
              </div>
              <div className="border-t border-white/20 pt-3 space-y-2">
                <PLRow label="Owner Withdrawals (Nelson)" value={annual.ownerDraws} testId="ann-owner-draws" />
                <PLRow label="Profit Retained in Business" value={annual.retained} bold testId="ann-retained" />
              </div>
            </section>

            {/* Insight cards */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="annual-insights">
              <InsightCard title="Best-performing Month" value={insights.best ? `${MONTH_LABELS[insights.best.monthIdx]} · ${formatCurrency(insights.best.netProfit)}` : "—"} sub={insights.best ? `${insights.best.netMargin.toFixed(1)}% margin` : ""} positive testId="insight-best" />
              <InsightCard title="Worst-performing Month" value={insights.worst ? `${MONTH_LABELS[insights.worst.monthIdx]} · ${formatCurrency(insights.worst.netProfit)}` : "—"} sub={insights.worst ? `${insights.worst.netMargin.toFixed(1)}% margin` : ""} testId="insight-worst" />
              <InsightCard title="Highest Revenue Month" value={insights.highestRev ? `${MONTH_LABELS[insights.highestRev.monthIdx]} · ${formatCurrency(insights.highestRev.revenue)}` : "—"} testId="insight-highest-rev" />
              <InsightCard title="Largest Expense Category" value={insights.largestCat ? insights.largestCat.name : "—"} sub={insights.largestCat ? formatCurrency(insights.largestCat.amount) : ""} testId="insight-largest-cat" />
            </section>

            {/* Trend list — simple sparkline */}
            <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-2" data-testid="annual-trend">
              <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Monthly Profit Trend</h2>
              <div className="grid grid-cols-12 gap-1.5 items-end h-24">
                {monthly.map((m, i) => {
                  const maxAbs = Math.max(1, ...monthly.map((x) => Math.abs(x.netProfit)));
                  const h = Math.round(Math.abs(m.netProfit) / maxAbs * 100);
                  const positive = m.netProfit >= 0;
                  return (
                    <div key={i} className="flex flex-col items-center justify-end" title={`${MONTH_LABELS[i]} · ${formatCurrency(m.netProfit)}`}>
                      <div className={`w-full rounded-t ${positive ? "bg-emerald-500" : "bg-red-500"} ${m.netProfit === 0 ? "opacity-20" : "opacity-90"}`} style={{ height: `${Math.max(2, h)}%` }} />
                      <span className="text-[9px] font-bold text-slate-500 mt-1">{MONTH_LABELS[i]}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Monthly comparison table */}
            <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="annual-comparison-table">
              <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-slate-50">
                <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>January – December Comparison</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[720px] w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left py-2 px-3 font-bold">Month</th>
                      <th className="text-right py-2 px-3 font-bold">Revenue</th>
                      <th className="text-right py-2 px-3 font-bold">Gross Profit</th>
                      <th className="text-right py-2 px-3 font-bold">Operating Exp.</th>
                      <th className="text-right py-2 px-3 font-bold">Net Profit</th>
                      <th className="text-right py-2 px-3 font-bold">Net Margin</th>
                      <th className="text-right py-2 px-3 font-bold">Owner Draws</th>
                      <th className="text-right py-2 px-3 font-bold">Retained</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthly.map((m, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`ann-row-${i + 1}`}>
                        <td className="py-2 px-3 font-mono font-semibold text-slate-700">{MONTH_LABELS[i]}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(m.revenue)}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(m.grossProfit)}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(m.operating + m.eventExpenses)}</td>
                        <td className={`py-2 px-3 text-right font-mono font-semibold ${m.netProfit > 0 ? "text-emerald-700" : m.netProfit < 0 ? "text-red-600" : "text-slate-500"}`}>{formatCurrency(m.netProfit)}</td>
                        <td className="py-2 px-3 text-right font-mono text-slate-600">{m.revenue > 0 ? `${m.netMargin.toFixed(1)}%` : "—"}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(m.ownerDraws)}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(m.retained)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                      <td className="py-3 px-3">Year Total</td>
                      <td className="py-3 px-3 text-right font-mono" data-testid="ann-total-revenue">{formatCurrency(annual.revenue)}</td>
                      <td className="py-3 px-3 text-right font-mono" data-testid="ann-total-gross">{formatCurrency(annual.grossProfit)}</td>
                      <td className="py-3 px-3 text-right font-mono" data-testid="ann-total-opex">{formatCurrency(annual.operating + annual.eventExpenses)}</td>
                      <td className={`py-3 px-3 text-right font-mono ${annual.netProfit > 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="ann-total-net">{formatCurrency(annual.netProfit)}</td>
                      <td className="py-3 px-3 text-right font-mono">{annual.revenue > 0 ? `${annual.netMargin.toFixed(1)}%` : "—"}</td>
                      <td className="py-3 px-3 text-right font-mono" data-testid="ann-total-draws">{formatCurrency(annual.ownerDraws)}</td>
                      <td className="py-3 px-3 text-right font-mono" data-testid="ann-total-retained">{formatCurrency(annual.retained)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 leading-relaxed">
              <strong>How this is calculated:</strong> Monthly totals from Per Event P&amp;L records + Transactions grouped by pl_treatment. Product Purchases and Asset Purchases are excluded from Net Profit (they appear in Cash Summary on Monthly P&amp;L only). Owner Withdrawals only reduce Profit Retained.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PLRow = ({ label, value, big = false, bold = false, text = false, color, testId }) => (
  <div className="flex items-center justify-between">
    <span className={`text-white/80 ${big ? "text-sm" : "text-xs"} uppercase tracking-wider font-semibold`}>{label}</span>
    <span className={`font-mono ${bold ? "font-bold" : "font-semibold"} ${big ? "text-2xl" : "text-base"} ${color || (typeof value === "number" && value < 0 ? "text-white/85" : "text-white")}`} data-testid={testId}>
      {text ? value : formatCurrency(value)}
    </span>
  </div>
);

const InsightCard = ({ title, value, sub, positive, testId }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid={testId}>
    <div className="flex items-start gap-3">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${positive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
        {positive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{title}</p>
        <p className="text-sm font-semibold text-slate-900 mt-1 truncate">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  </div>
);

export default AnnualPL;
